import { atomFactory } from '@utils/recoil/atomFactory';
import {
    selectorFamily,
    useRecoilValue,
    RecoilValue,
    GetRecoilValue,
    RecoilValueReadOnly,
    SerializableParam,
} from 'recoil';

export type RecoilOpts = {
    get: GetRecoilValue;
};
export type SelectorFamilyFactoryGetFn<T> = (
    opts: RecoilOpts,
    param: SerializableParam,
) => T | Promise<T> | RecoilValue<T>;
export type SelectorFamilyFactoryGetParam<T> = Parameters<
SelectorFamilyFactoryGetFn<T>
>;
export type SelectorFamilyFactoryR<T> = {
    family: (param: SerializableParam) => RecoilValueReadOnly<T>;
    hook: (
        param?: SerializableParam,
    ) => {
        value: T;
        invalidateCache: () => void;
    };
};

export type SelectorFamilyFactoryParam<T> = {
    key: string;
    get: SelectorFamilyFactoryGetFn<T>;
};

export const selectorFamilyFactory = <T>(
    selectorFamilyParam: SelectorFamilyFactoryParam<T>,
): SelectorFamilyFactoryR<T> => {
    const { key, get } = selectorFamilyParam;

    // hack to force invalidation of cache from...
    // ... https://github.com/facebookexperimental/Recoil/issues/85#issuecomment-629862897
    const { atom: forceUpdate, hook: useForceUpdate } = atomFactory({
        key: `${key}-force-update-selector-family-atom`,
        default: 0,
    });

    const family = selectorFamily({
        key,
        get: (param: SerializableParam) => (opts) => {
            opts.get(forceUpdate);
            return get(opts, param);
        },
    });

    const useSelectorFamily = (param?: SerializableParam) => {
        const { value: count, set: increment } = useForceUpdate();
        const invalidateCache = () => increment(count + 1);
        const value = useRecoilValue(family(param));

        return {
            value,
            invalidateCache,
        };
    };

    return {
        family,
        hook: useSelectorFamily,
    };
};
